import { fetchEventSource } from '@microsoft/fetch-event-source';

import { HTTP_TYPES } from '@/lib/enums';
import api from '@/api/api';
import { useUserStore } from '@/stores/user.store';

import type {
	DeadLetterLocator,
	DeadLetterActionMessage,
	StreamMessagesOptions,
	StreamMessagesHandlers,
	ServiceBusMessage,
} from './types/management-servicebus.types';
import {
	USE_MOCK_DATA,
	mockDelay,
	MOCK_STATISTICS,
	MOCK_QUEUES,
	MOCK_TOPICS,
	MOCK_SUBSCRIPTIONS,
	MOCK_DEAD_LETTER,
	locatorKey,
	locatorIds,
	emulateMessageStream,
} from './mocks/management-servicebus.mock';

// Re-export the types so existing consumers keep importing them from this module.
export type {
	ServiceBusStatistics,
	Queue,
	Topic,
	Subscription,
	DeadLetterMessage,
	ServiceBusMessage,
	SubQueue,
	StreamMessagesOptions,
	MessagePage,
	StreamMessagesHandlers,
	DeadLetterLocator,
	DeadLetterActionMessage,
	RequeueDeadLetterResult,
	DeleteDeadLetterResult,
} from './types/management-servicebus.types';

const API_VERSION = 'v1';

/**
 * Base URL for the intermediate management service. The admin UI never talks to
 * Azure Service Bus directly — it calls this service, which performs all SDK
 * work (peek, receive-from-DLQ, resubmit-to-parent, purge).
 *
 * `VITE_DEPOT_API_ENDPOINTS` is the apim root and ends with a trailing slash.
 */
const MANAGEMENT_BASE = `${import.meta.env.VITE_DEPOT_API_ENDPOINTS}management/servicebus`;

/** Build the batch requeue/delete URL for a locator. */
const deadLetterActionPath = (locator: DeadLetterLocator, action: 'requeue' | 'delete'): string => {
	return locator.kind === 'queue'
		? `${MANAGEMENT_BASE}/dead-letter-queue-messages/${encodeURIComponent(
				locator.queueName,
			)}/${action}`
		: `${MANAGEMENT_BASE}/dead-letter-topic-messages/${encodeURIComponent(
				locator.topicName,
			)}/${encodeURIComponent(locator.subscriptionName)}/${action}`;
};

const response = {
	/**
	 * Namespace statistics for the overview strip.
	 * GET /management/servicebus/statistics?refresh={bool}
	 *
	 * `refresh=true` forces a live recount against Azure (potentially slow);
	 * the default `false` returns the service's cached values.
	 */
	async getStatistics(refresh = false): Promise<any> {
		if (USE_MOCK_DATA) {
			return mockDelay({ data: MOCK_STATISTICS });
		}

		return await api(
			HTTP_TYPES.GET,
			`${MANAGEMENT_BASE}/statistics`,
			undefined,
			{ refresh },
			'application/json',
			API_VERSION,
		);
	},
	/** List all queues. GET /management/servicebus/all-queues */
	async getQueues(): Promise<any> {
		if (USE_MOCK_DATA) {
			return mockDelay({ data: { queues: MOCK_QUEUES } });
		}

		return await api(
			HTTP_TYPES.GET,
			`${MANAGEMENT_BASE}/all-queues`,
			undefined,
			undefined,
			'application/json',
			API_VERSION,
		);
	},
	/** List all topics. GET /management/servicebus/all-topics */
	async getTopics(): Promise<any> {
		if (USE_MOCK_DATA) {
			return mockDelay({ data: { topics: MOCK_TOPICS } });
		}

		return await api(
			HTTP_TYPES.GET,
			`${MANAGEMENT_BASE}/all-topics`,
			undefined,
			undefined,
			'application/json',
			API_VERSION,
		);
	},
	/** List a topic's subscriptions. GET /management/servicebus/topic-subscriptions/:topicName */
	async getSubscriptions(topicName: string): Promise<any> {
		if (USE_MOCK_DATA) {
			return mockDelay({
				data: { topicName, subscriptions: MOCK_SUBSCRIPTIONS[topicName] ?? [] },
			});
		}

		return await api(
			HTTP_TYPES.GET,
			`${MANAGEMENT_BASE}/topic-subscriptions/${encodeURIComponent(topicName)}`,
			undefined,
			undefined,
			'application/json',
			API_VERSION,
		);
	},
	/**
	 * Stream a page of messages for a queue or subscription as Server-Sent
	 * Events. The locator selects the endpoint:
	 *   queue        → GET /management/servicebus/queue-messages/:queueName
	 *   subscription → GET /management/servicebus/topic-messages/:topicName/:subscriptionName
	 *
	 * Emits one `message` event per row, a terminal `page-end`
	 * ({ hasMore, nextSequenceNumber? }), and an `error` event on mid-stream
	 * failure. Browsing is peek-only. Paginate with `limit` and the opaque
	 * `fromSequenceNumber` cursor.
	 */
	async streamMessages(
		locator: DeadLetterLocator,
		options: StreamMessagesOptions,
		handlers: StreamMessagesHandlers,
		signal?: AbortSignal,
	): Promise<void> {
		const subQueue = options.subQueue ?? 'main';

		if (USE_MOCK_DATA) {
			await emulateMessageStream(locator, subQueue, options, handlers);
			return;
		}

		const path =
			locator.kind === 'queue'
				? `queue-messages/${encodeURIComponent(locator.queueName)}`
				: `topic-messages/${encodeURIComponent(locator.topicName)}/${encodeURIComponent(
						locator.subscriptionName,
					)}`;

		const query = new URLSearchParams({ subQueue });
		if (options.limit != null) {
			query.set('limit', String(options.limit));
		}

		if (options.fromSequenceNumber) {
			query.set('fromSequenceNumber', options.fromSequenceNumber);
		}

		const url = `${MANAGEMENT_BASE}/${path}?${query.toString()}`;

		// Native EventSource cannot send an Authorization header, so the stream
		// runs over fetch with the same Bearer token the rest of the API uses.
		const userStore = useUserStore();
		const token = userStore.accessToken ?? (await userStore.ensureAccessToken(false));

		await fetchEventSource(url, {
			method: 'GET',
			signal,
			openWhenHidden: true,
			headers: {
				Authorization: token ? `Bearer ${token}` : '',
				'X-Api-Version': API_VERSION,
				Accept: 'text/event-stream',
			},
			async onopen(res) {
				const contentType = res.headers.get('content-type') ?? '';
				if (res.ok && contentType.includes('text/event-stream')) {
					return;
				}

				throw new Error(`Stream failed: ${res.status}`);
			},
			onmessage(ev) {
				if (ev.event === 'page-end') {
					handlers.onPageEnd(ev.data ? JSON.parse(ev.data) : { hasMore: false });
				} else if (ev.event === 'error') {
					handlers.onError(ev.data ? JSON.parse(ev.data) : undefined);
				} else {
					// Default (unnamed) and `message` events both carry a row.
					handlers.onMessage(JSON.parse(ev.data) as ServiceBusMessage);
				}
			},
			onerror(err) {
				// Rethrow so the library stops its automatic reconnect and the
				// caller's try/catch surfaces the failure as an error state.
				throw err;
			},
		});
	},
	/**
	 * Peek the dead-letter messages for a queue or a topic subscription.
	 * GET /management/servicebus/dead-letter-messages?queue=… | ?topic=…&subscription=…
	 */
	async getDeadLetterMessages(locator: DeadLetterLocator): Promise<any> {
		if (USE_MOCK_DATA) {
			return mockDelay({ data: { messages: MOCK_DEAD_LETTER[locatorKey(locator)] ?? [] } });
		}

		const params =
			locator.kind === 'queue'
				? { queue: locator.queueName }
				: { topic: locator.topicName, subscription: locator.subscriptionName };

		return await api(
			HTTP_TYPES.GET,
			`${MANAGEMENT_BASE}/dead-letter-messages`,
			undefined,
			params,
			'application/json',
			API_VERSION,
		);
	},
	/**
	 * Requeue dead-letter messages back to their parent entity, completing them
	 * from the dead-letter sub-queue. Batch endpoint, selected by locator:
	 *   queue        → POST /dead-letter-queue-messages/:queueName/requeue
	 *   subscription → POST /dead-letter-topic-messages/:topicName/:subscriptionName/requeue
	 *
	 * Body `{ messages }`; the response splits the requested messages into
	 * `requeuedMessages` (completed) and `missingMessages` (not found at receive).
	 */
	async requeueDeadLetter(
		locator: DeadLetterLocator,
		messages: DeadLetterActionMessage[],
	): Promise<any> {
		if (USE_MOCK_DATA) {
			return mockDelay({
				data: { ...locatorIds(locator), requeuedMessages: messages, missingMessages: [] },
			});
		}

		return await api(
			HTTP_TYPES.POST,
			deadLetterActionPath(locator, 'requeue'),
			{ messages },
			undefined,
			'application/json',
			API_VERSION,
		);
	},
	/**
	 * Permanently delete dead-letter messages without resending. Batch endpoint,
	 * selected by locator:
	 *   queue        → POST /dead-letter-queue-messages/:queueName/delete
	 *   subscription → POST /dead-letter-topic-messages/:topicName/:subscriptionName/delete
	 *
	 * Body `{ messages }`; the response splits the requested messages into
	 * `deletedMessages` and `missingMessages`.
	 */
	async deleteDeadLetter(
		locator: DeadLetterLocator,
		messages: DeadLetterActionMessage[],
	): Promise<any> {
		if (USE_MOCK_DATA) {
			return mockDelay({
				data: { ...locatorIds(locator), deletedMessages: messages, missingMessages: [] },
			});
		}

		return await api(
			HTTP_TYPES.POST,
			deadLetterActionPath(locator, 'delete'),
			{ messages },
			undefined,
			'application/json',
			API_VERSION,
		);
	},
};

export default response;
